import { createFileRoute, useRouter } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import Stack from '@mui/material/Stack';
import { PageHeader } from '@/common/components/page-header/PageHeader.tsx';
import { Trans } from '@lingui/react/macro';
import EditIcon from '@mui/icons-material/Edit';
import { dispenserQueryKeys, dispenserQueries } from '@/features/dispensers/queries.ts';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { DispenserStatusChip } from '@/features/dispensers/components/dispenser-status-chip/DispenserStatusChip.tsx';
import { DispenserDetail } from '@/features/dispensers/components/dispenser-detail/DispenserDetail.tsx';
import { getLastEvent } from '@/features/dispensers/utils/lastEvent.ts';
import { RouterButtonLink } from '@/common/components/router-links/RouterButtonLink.tsx';
import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { MoreActionsDropdown } from '@/common/components/more-actions-dropdown/MoreActionsDropdown.tsx';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useConfirmation } from '@/common/components/confirmation-dialog/hooks/useConfirmation.tsx';
import { useAlert } from '@/common/components/alert/hooks/useAlert.tsx';
import { DispenserService } from '@/features/dispensers/services/DispenserService.ts';

interface ApiErrorResponse {
  statusCode: number;
  message: string;
  errors?: Record<string, string[]>;
}

export const Route = createFileRoute('/dispensers/$dispenserId/')({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(dispenserQueries.detail(params.dispenserId));
    await context.queryClient.ensureQueryData(dispenserQueries.lastEvents(params.dispenserId));
  },
});

function RouteComponent() {
  const { dispenserId } = Route.useParams();
  const router = useRouter();
  const queryClient = useQueryClient();
  const showConfirmation = useConfirmation();
  const showAlert = useAlert();

  const { data: dispenser } = useSuspenseQuery(dispenserQueries.detail(dispenserId));
  const { data: lastEvents } = useSuspenseQuery(dispenserQueries.lastEvents(dispenserId));
  const dispenserSn = dispenser.sn;

  const showCannotDeleteAlert = () =>
    showAlert({
      severity: 'error',
      title: <Trans>Cannot Delete</Trans>,
      message: (
        <Trans>
          The dispenser <strong>{dispenserSn}</strong> cannot be deleted because it has already
          recorded operational data and service events.
        </Trans>
      ),
      secondaryMessage: (
        <Trans>
          Deletion is reserved for correcting creation errors. To preserve the service history, this
          record must remain in the system.
        </Trans>
      ),
      acknowledgeLabel: <Trans>I understand</Trans>,
      maxWidth: 'xs',
    });

  const showGenericDeleteErrorAlert = () =>
    showAlert({
      severity: 'error',
      title: <Trans>Error</Trans>,
      message: (
        <Trans>
          An unknown error occurred during deletion of the dispenser. Please try again later or
          contact support.
        </Trans>
      ),
      acknowledgeLabel: <Trans>I understand</Trans>,
      maxWidth: 'xs',
    });

  const deleteMutation = useMutation({
    mutationFn: () => DispenserService.delete(dispenserId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: dispenserQueryKeys.all });
      await router.navigate({ to: '/dispensers' });
    },
    onError: async (error: AxiosError<ApiErrorResponse>) => {
      const hasEventsError = error.response?.data?.errors?.['equipment.HasEvents'];
      if (hasEventsError) {
        await showCannotDeleteAlert();
      } else {
        await showGenericDeleteErrorAlert();
      }
    },
  });

  const handleDelete = async () => {
    const confirmed = await showConfirmation({
      title: <Trans>Delete dispenser</Trans>,
      description: (
        <Trans>
          Are you sure you want to delete dispenser <strong>{dispenserSn}</strong>? This action
          cannot be undone.
        </Trans>
      ),
      confirmLabel: <Trans>Delete</Trans>,
      variant: 'dangerous',
    });

    if (confirmed) {
      deleteMutation.mutate();
    }
  };

  return (
    <Stack gap={4}>
      <PageHeader
        title={
          <Stack direction="row" alignItems="center" gap={1}>
            {dispenser.sn}{' '}
            <DispenserStatusChip dispenser={dispenser} lastEvent={getLastEvent(lastEvents)} />
          </Stack>
        }
        subtitle={<Trans>Dispenser details and configuration</Trans>}
        actions={
          <>
            <RouterButtonLink
              to="/dispensers/$dispenserId/timeline"
              params={{ dispenserId }}
              size="small"
              startIcon={<ViewTimelineOutlinedIcon />}
            >
              <Trans>Timeline</Trans>
            </RouterButtonLink>
            <RouterButtonLink
              to="/dispensers/$dispenserId/edit"
              params={{ dispenserId }}
              size="small"
              startIcon={<EditIcon />}
            >
              <Trans>Edit</Trans>
            </RouterButtonLink>
            <MoreActionsDropdown
              size="small"
              color="primary"
              actions={[
                {
                  key: 'delete',
                  content: (
                    <>
                      <ListItemIcon>
                        <DeleteRoundedIcon color="error" />
                      </ListItemIcon>
                      <ListItemText>
                        <Trans>Delete</Trans>
                      </ListItemText>
                    </>
                  ),
                  onClick: handleDelete,
                },
              ]}
            />
          </>
        }
      />
      <DispenserDetail dispenser={dispenser} lastEvents={lastEvents} />
    </Stack>
  );
}
